import { z, ZodType } from 'zod';
import { Inbox } from '../../inboxes/models/inbox';
import { listResponse } from '../../utils/listResponse';
import { AutomationLevel } from '../models/automationLevel';
import { Queue } from '../models/queue';
import { queueSchema } from '../models/queue.schema';
import { queueListQuerySchema, queueListSideloadSchema } from './list.schema';

export type QueueListQuery = {
  pageSize?: number;
  page?: number;
  ordering?: string[];
  id?: number[];
  workspace?: number[];
  deleting?: boolean;
  name?: string;
  dedicatedEngine?: number[];
  genericEngine?: number[];
  engine?: number[];
  enginesFilterMode?: 'OR' | 'AND';
  hooks?: number[];
  automationLevel?: AutomationLevel[];
};

/**
 * @noSchema
 */
type WithFields<T, F> = T & { fields?: F[] };

/**
 * @noSchema
 */
export type QueueListQueryWithFields<T extends keyof Queue = keyof Queue> =
  WithFields<QueueListQuery, T>;

export type QueueListSideload = {
  inboxes: Array<Inbox>;
};

export const list = <FieldsT extends keyof Queue = keyof Queue>(
  query: WithFields<QueueListQuery, FieldsT> = {}
) => {
  const fieldsObject = query.fields?.reduce<Partial<Record<keyof Queue, true>>>(
    (acc, curr) => Object.assign(acc, { [curr]: true }),
    {}
  );

  return {
    endpoint: `/queues`,
    method: 'GET',
    responseSchema: listResponse(
      fieldsObject ? queueSchema.pick(fieldsObject) : queueSchema
    ),
    query: { ...query, deleting: false, includeTotal: true },
    querySchema: queueListQuerySchema
      .and(
        z.object({
          fields: z.array(z.string()).optional(),
        })
      )
      .and(
        z.object({ includeTotal: z.boolean().optional() })
      ) as unknown as ZodType<WithFields<QueueListQuery, FieldsT>>,
    sideloadSchema: queueListSideloadSchema,
  } as const;
};
